import { Anchor, Button, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { getCloudMetadata } from "../shared/getCurrentUser";
import { queryKeys, RestApi } from "../shared/RestApi";
import { NavBarContainer } from "./NavBarContainer";

declare const Stripe: (key: string) => {
  redirectToCheckout: (opts: {
    sessionId: string;
  }) => Promise<{ error?: { message: string } }>;
};

export function BuyPage() {
  const { t } = useTranslation();
  const [payError, setPayError] = React.useState<string | null>(null);
  const [paying, setPaying] = React.useState(false);
  const metadata = getCloudMetadata();

  const { data, isPending, isError } = useQuery({
    queryKey: queryKeys.buySession,
    queryFn: () => RestApi.createBuySession(),
  });

  const onPay = async () => {
    if (!data || data.bought) return;
    setPaying(true);
    setPayError(null);
    try {
      const stripe = Stripe(metadata.StripePublishableKey);
      const result = await stripe.redirectToCheckout({
        sessionId: data.sessionId,
      });
      if (result.error) {
        setPayError(result.error.message);
      }
    } catch {
      setPayError(t("buyPage.payError"));
    } finally {
      setPaying(false);
    }
  };

  const errorMessage = isError
    ? t("buyPage.loadError")
    : payError;

  return (
    <NavBarContainer variant="marketing" title={t("buyPage.title")}>
      <Stack gap="md" maw={560}>
        {errorMessage && <Text c="red">{errorMessage}</Text>}
        {isPending && <Text>{t("buyPage.loading")}</Text>}

        {data?.bought && (
          <>
            <Title order={3}>{t("buyPage.thanks")}</Title>
            <Text>{t("buyPage.fullAccess")}</Text>
            <Text>
              {t("buyPage.paymentId", {
                method: data.paymentMethod ?? "",
                id: data.paymentId ?? "",
              })}
            </Text>
            <Anchor component={Link} to="/userinfo">
              {t("buyPage.checkAccount")}
            </Anchor>
          </>
        )}

        {data && !data.bought && (
          <>
            <Text>{t("buyPage.price")}</Text>
            <Button onClick={onPay} loading={paying}>
              {t("buyPage.payWithCard")}
            </Button>
            <Text>
              <Anchor component={Link} to="/viewdistributors">
                {t("buyPage.contactDistributor")}
              </Anchor>
            </Text>
            <Text>
              <Anchor component={Link} to="/distributorportal">
                {t("buyPage.becomeDistributor")}
              </Anchor>
            </Text>
          </>
        )}
      </Stack>
    </NavBarContainer>
  );
}
